import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Checkbox from '../../../Checkbox/Checkbox';
import Input from '../../../Input/Input';
import './MultiSelectOptionsList.scss';
import Text from '../../../Text/Text';

type Option = { label: string; value: string };

const MultiSelectOptionsList = forwardRef(
  (
    {
      inputId,
      options,
      selectedOptions,
      isDropdownOpen,
      onChange,
      dropdownTabIndex,
      rightAligned,
    }: {
      inputId: string;
      options?: Option[];
      selectedOptions: string[];
      isDropdownOpen: boolean;
      onChange: (v: unknown) => void;
      dropdownTabIndex?: number;
      rightAligned: boolean;
    },
    ref,
  ) => {
    const [searchTerm, setSearchTerm] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
      if (isDropdownOpen && inputRef.current) {
        setTimeout(() => {
          //@ts-expect-error refs are quirky
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          inputRef?.current?.focus();
        }, 100);
      }
    }, [isDropdownOpen]);

    const filteredOptions = useMemo(() => {
      return options?.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }, [options, searchTerm]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    };

    const handleOptionSelect = (option: Option) => {
      const optionSelected = selectedOptions?.find(
        selectedOption => selectedOption === option.value,
      );
      if (optionSelected) {
        const filteredSelectedOptions = selectedOptions?.filter(
          selectedOption => selectedOption !== option.value,
        );
        onChange({ key: inputId, value: filteredSelectedOptions });
        return;
      }
      onChange({ key: inputId, value: [...selectedOptions, option.value] });
      return;
    };

    return (
      <ul
        className={`multi-select-dropdown${isDropdownOpen ? ' active' : ''}${rightAligned ? ' right-aligned' : ''}`}
        role="listbox"
        id={`${inputId}-select-dropdown`}
        tabIndex={dropdownTabIndex}
        // @ts-expect-error refs are quirky
        ref={ref}
      >
        <div className="input-container-spacing">
          <Input
            inputId="option-search"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        {filteredOptions?.map((option, i) => {
          return (
            <li role="option" key={i} className="option-with-checkbox">
              <Text type="body">{option.label}</Text>
              <Checkbox
                inputId={inputId}
                dataTestId={`${inputId}-${option.value}`}
                isChecked={selectedOptions?.some(selectedOption => selectedOption === option.value)}
                onChange={() => handleOptionSelect(option)}
              />
            </li>
          );
        })}
        {filteredOptions?.length === 0 && (
          <div className="body no-options-available-text">
            <Text type="body">No Results Found</Text>
          </div>
        )}
      </ul>
    );
  },
);

MultiSelectOptionsList.displayName = 'MultiSelectOptionsList';

export default MultiSelectOptionsList;
